import React from "react";
import ClickableBatch from "../../../clickableBatch/clickableBatch";

/** Cell value shape for {@link ClickableBadgeWithOnClickEmitter}. */
interface ClickableBadgeWithOnClickEmitterValue {
  /** Row/order identifier emitted back to the table consumer on click. */
  id: string;
  /** Text rendered inside the clickable badge. */
  value: string;
}

/** Props for {@link ClickableBadgeWithOnClickEmitter}. */
interface ClickableBadgeWithOnClickEmitterProps {
  /** Cell value carrying the identifier and the badge label. */
  value: ClickableBadgeWithOnClickEmitterValue;
  /**
   * Optional click handler. When provided, it is invoked with the cell's
   * `id` so a parent `CustomTable` consumer can react to the badge click.
   */
  onClick?: (id: string) => void;
}

/**
 * Table cell that renders a clickable badge and emits the row's `id` via an
 * optional `onClick` callback instead of opening a modal itself. The wrapping
 * `div` stops click propagation so the table row's own click handler does not
 * also fire.
 *
 * @param props.value - Cell value with `id` and the badge `value` text.
 * @param props.onClick - Optional handler invoked with the cell `id` on click.
 */
export const ClickableBadgeWithOnClickEmitter: React.FC<
  ClickableBadgeWithOnClickEmitterProps
> = ({ value, onClick }) => {
  return (
    <div
      className="table-cell-clickable-badge w-full"
      onClick={(e) => e.stopPropagation()}
    >
      <ClickableBatch text={value.value} onClick={() => onClick?.(value.id)} />
    </div>
  );
};

export default ClickableBadgeWithOnClickEmitter;
